import React, { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid'; // Import the uuid library
import { Form } from 'react-bootstrap';
import placesService, { IActivityBias } from '../services/places.service';
import { ISearchBiasModel } from '../Models/search-bias.model';
import { IPlacesModel } from './modals/activity/use-add-activity';
import { InlineLoading } from './UI/loading';

export interface ActivityAutocompleteModel {
  id: string;
  description: string;
  name: string;
  latitude?: number;
  longitude?: number;
  placesId?: string; // Optional field for placesId
  googlePlacesId?: string; // Optional field for googlePlacesId
  types?: string[];
  primaryType?: string;
  websiteUrl?: string;
  searchText?: string;
  activityId?: string;
  imageUrl?: string;
  location?: string; // Optional field for location
  addressComponents?: any[];
}

type ActivityAutocompleteProps = {
  label?: string;
  placeholder?: string;
  setPlace: (place: ActivityAutocompleteModel) => void; // Optional callback to set the placeId
  bias?: ISearchBiasModel; // <-- Add this line
  place?: IPlacesModel; // Optional place prop for initial value
  dropdownPosition?: 'below' | 'above';
};

const ActivityAutocomplete: React.FC<ActivityAutocompleteProps> = ({
  label,
  placeholder = 'Enter a location',
  setPlace = () => {},
  bias = undefined, // Default to undefined if not provided
  place = undefined, // Optional place prop for initial value
  dropdownPosition = 'below'
}: ActivityAutocompleteProps) => {
  // State to manage the input value, session token, suggestions, and suggestion click state
  const [input, setInput] = useState('');
  const [sessionToken, setSessionToken] = useState(uuidv4());
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSuggestionClicked, setIsSuggestionClicked] = useState(false); // New state
  const [isLoadingDetails, setIsLoadingDetails] = useState(false); // Loading state for fetchDetails
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false); // Loading state for suggestions
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);

    return () => {
      window.removeEventListener('resize', updateIsMobile);
    };
  }, []);

  useEffect(() => {
    if (suggestions.length === 0 || !containerRef.current) return;

    const updateDropdownPosition = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const openBelow = isMobile || dropdownPosition === 'below';
      const verticalGap = 4;
      const maxWidth = Math.min(rect.width, window.innerWidth - rect.left - 8);

      setDropdownStyle({
        position: 'fixed',
        left: Math.max(8, rect.left),
        top: openBelow ? rect.bottom + verticalGap : undefined,
        bottom: openBelow
          ? undefined
          : Math.max(8, window.innerHeight - rect.top + verticalGap),
        width: Math.max(120, maxWidth),
        maxHeight: isMobile ? '45vh' : '40vh',
        overflowY: 'auto',
        zIndex: 9999,
        border: '1px solid #e5c9cf',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(128,0,32,0.1)',
        backgroundColor: '#fff'
      });
    };

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [suggestions.length, dropdownPosition, isMobile]);

  // If place prop changes, update input and fetch details
  useEffect(() => {
    console.log('Place prop bias:', bias);
    if (place && place.placesId) {
      setInput(place.name);
      console.log('use effect: Fetching details for place:', place);
      fetchDetails({
        id: place.placesId,
        name: place.name,
        location: bias?.location || '',
        latitude: bias?.lat || 0,
        longitude: bias?.lng || 0,
        description: '',
        types: place.types || [],
        placesId: place.placesId
      } as ActivityAutocompleteModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  const fetchDetails = async (selection: ActivityAutocompleteModel) => {
    if (selection.id) {
      setIsLoadingDetails(true); // Start loading
      try {
        console.log('Fetching details for selection:', selection);

        const fetchedPlace = await placesService.getPlaceDetails(
          selection.id,
          sessionToken
        );

        if (fetchedPlace) {
          // Update the selection object with the fetched details
          selection.activityId = fetchedPlace.id || '';
          selection.imageUrl = fetchedPlace.imageUrl || '';
          selection.placesId = selection.id;
          selection.googlePlacesId = selection.id;
          selection.name = fetchedPlace.title || fetchedPlace.name || '';
          selection.description =
            fetchedPlace.description || fetchedPlace.title;
          selection.latitude = fetchedPlace.location.latitude;
          selection.longitude = fetchedPlace.location.longitude;
          selection.types = fetchedPlace.types || [];
          selection.websiteUrl = fetchedPlace.websiteUrl || '';
          selection.primaryType = fetchedPlace.primaryType || '';
          selection.addressComponents = fetchedPlace.addressComponents || [];
          setSessionToken(uuidv4());
          console.log('Fetched place details:', selection);
          console.log('fetchedPlace:', fetchedPlace);
          setPlace(selection);
        }
      } catch (error) {
        console.error('Error fetching details:', error);
      } finally {
        setIsLoadingDetails(false); // Stop loading
      }
    }
  };

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (input.length > 0) {
        setIsLoadingSuggestions(true); // Start loading suggestions
        try {
          console.log('[ActivityAutocomplete] Input bias:', bias);

          // Convert ISearchBiasModel to IActivityBias format
          // Only create activityBias if lat and lng are present
          const activityBias: IActivityBias | undefined =
            bias && bias.lat !== undefined && bias.lng !== undefined
              ? {
                  lat: bias.lat,
                  lng: bias.lng,
                  radius: bias.radius || 50000
                }
              : undefined;

          console.log(
            '[ActivityAutocomplete] Converted activityBias:',
            activityBias
          );

          const res = await placesService.getAutocompleteSuggestions(
            input,
            sessionToken,
            activityBias
          );

          console.log('[ActivityAutocomplete] Received response:', res);
          console.log(
            '[ActivityAutocomplete] res.predictions:',
            res.predictions
          );
          console.log('[ActivityAutocomplete] res.status:', res.status);

          if (res.predictions && res.predictions.length > 0) {
            const tmpSuggestions = res.predictions.map((prediction: any) => {
              // Support both legacy and new prediction formats
              const placePrediction = prediction.placePrediction || prediction;
              const placeId = placePrediction.placeId || prediction.place_id;
              const text = placePrediction.text?.text || prediction.description;
              const mainText =
                placePrediction.structuredFormat?.mainText?.text ||
                prediction.structured_formatting?.main_text ||
                text;
              const types = placePrediction.types || prediction.types || [];

              return {
                id: placeId,
                description: text,
                name: mainText,
                searchText: text + ' ' + (types[0] || ''),
                types: types
              } as ActivityAutocompleteModel;
            });
            console.log('Suggestions:', tmpSuggestions);
            setSuggestions(tmpSuggestions);
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error('Error fetching autocomplete suggestions:', error);
          setSuggestions([]);
        } finally {
          setIsLoadingSuggestions(false); // Stop loading suggestions
        }
      } else {
        setSuggestions([]);
        setIsLoadingSuggestions(false); // Ensure loading is stopped when input is empty
      }
    },
    [bias, sessionToken]
  );

  const ChangeIputAndFetchSuggestions = (
    value: string,
    fetch: boolean = true
  ) => {
    setInput(value);
    if (fetch) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    ChangeIputAndFetchSuggestions(value);
  };

  const handleInputBlur = () => {
    // Delay clearing suggestions to allow onClick to fire first
    setTimeout(() => {
      if (!isSuggestionClicked) {
        setSuggestions([]);
      }
      setIsSuggestionClicked(false); // Reset the flag
    }, 200);
  };

  // const handleUseAutoCompleteInputChange = (checked: boolean) => {
  //   setSaveToMap(checked);
  //   if (checked) {
  //     fetchSuggestions(input);
  //   } else {
  //     setSuggestions([]);
  //   }
  // };

  const handleAddClick = (selection: ActivityAutocompleteModel) => {
    setIsSuggestionClicked(true); // Set the flag to true
    fetchDetails(selection);
    ChangeIputAndFetchSuggestions(selection.name, false);
    setSuggestions([]);
  };

  return (
    <div
      className='activity-autocomplete'
      style={{ position: 'relative', width: '100%' }}
      ref={containerRef}
    >
      <div className=''>
        <Form.Group
          controlId='titleInput'
          className='mb-0'
        >
          {label === '' && <Form.Label>{label}</Form.Label>}
          <Form.Control
            className='mt-0 rounded-pill'
            type='text'
            value={input}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            autoComplete='off'
            disabled={isLoadingDetails} // Disable input while loading
          />
          {isLoadingDetails && (
            <div className='mt-2'>
              <InlineLoading text='Fetching location details...' />
            </div>
          )}
        </Form.Group>
      </div>
      {isLoadingSuggestions && (
        <div className='mt-2'>
          <InlineLoading text='Searching locations...' />
        </div>
      )}
      {suggestions.length > 0 && !isLoadingSuggestions && (
        <div
          onMouseDown={(e) => e.preventDefault()}
          style={dropdownStyle}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '7px 10px',
                backgroundColor: index % 2 === 0 ? '#fdf6f7' : '#fceef0',
                borderBottom:
                  index < suggestions.length - 1 ? '1px solid #f0dde0' : 'none',
                gap: '8px'
              }}
            >
              <button
                onClick={() => handleAddClick(suggestion)}
                style={{
                  flexShrink: 0,
                  background: '#800020',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  lineHeight: '1.5'
                }}
              >
                + Add
              </button>
              <span
                style={{
                  fontSize: '13px',
                  color: '#4a1020',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'default'
                }}
                title={suggestion.description}
              >
                {suggestion.description}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityAutocomplete;
